import {
  MagnifyingGlassIcon,
  PencilSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import clsx from "clsx";
import { formatListName } from "@/shared/utils/formatListName";
import type { ListEntry } from "../listEntries";

/**
 * The parts the builder's two lists — yours and the Newsquawk library — are
 * made of. Deliberately the same parts as a widget's filter and highlight
 * menus (MatrixSection): search box, grey group labels, one row per list, the
 * Combined chip, the list icon for a look inside. Managing lists here should
 * be the gesture people already make there.
 */

/** As the filter panel's search box, clear button included. */
export function ListSearch({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative">
      <MagnifyingGlassIcon className="absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-gray-500" />
      <input
        type="text"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder.replace(/\.+$/, "")}
        className="w-full rounded border border-gray-700 bg-gray-900 py-1.5 pl-8 pr-8 text-xs text-gray-200 placeholder:text-gray-500 focus:border-squawk-blue focus:outline-none"
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 border-0 p-0.5 text-gray-500 transition-colors hover:text-gray-300"
          aria-label="Clear search text"
        >
          <XMarkIcon className="size-3" />
        </button>
      )}
    </div>
  );
}

/**
 * A bordered panel with a title, a count, room for actions, and — like a
 * widget menu — its own search box directly under the header.
 */
export function ListPanel({
  title,
  count,
  actions,
  search,
  footer,
  children,
}: {
  title: string;
  count: number;
  actions?: React.ReactNode;
  /** Omit to leave the panel without one, e.g. while it has nothing in it. */
  search?: { value: string; onChange: (value: string) => void; placeholder: string };
  footer?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <section
      aria-label={title}
      className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden rounded-md border border-gray-700 bg-gray-900"
    >
      {/* Fixed height, so two panels side by side line up whatever sits in
          their actions slot — a button there is taller than a hint. */}
      <div className="flex h-10 shrink-0 items-center gap-2 border-b border-gray-700/30 px-3">
        <h2 className="text-xs font-semibold uppercase tracking-[0.1em] text-gray-300">
          {title}
        </h2>
        <span className="font-mono text-xs tabular-nums text-gray-500">
          {count}
        </span>
        {actions && (
          <div className="ml-auto flex items-center gap-2">{actions}</div>
        )}
      </div>
      {search && (
        <div className="shrink-0 border-b border-gray-700/30 px-3 py-2">
          <ListSearch {...search} />
        </div>
      )}
      {/* Capped like the menus' lists, about nine rows, so the panel stays
          a compact block under the cards with the headline strip below. */}
      <div className="max-h-56 min-h-0 flex-1 overflow-y-auto px-3 py-1.5 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
        {children}
      </div>
      {footer}
    </section>
  );
}

/** As MatrixSection's group labels ("Yours", "Newsquawk"). */
export function GroupLabel({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-1 pb-0.5 pt-1 text-2xs font-semibold uppercase tracking-wider text-gray-500">
      {children}
    </p>
  );
}

export function EmptyNote({ children }: { children: React.ReactNode }) {
  return (
    <p className="px-4 py-6 text-center text-xs text-gray-500">{children}</p>
  );
}

/** As MatrixSection's chip: filled and sentence case, so it qualifies the
 *  name rather than competing with it. */
function CombinedChip() {
  return (
    <span
      title="Several watchlists joined with AND/OR rules"
      className="shrink-0 rounded bg-gray-700/70 px-1.5 py-px text-2xs text-gray-300"
    >
      Combined
    </span>
  );
}

/**
 * One list.
 *
 * A click previews it in the headline strip below — the quickest way to judge
 * a list, and one a second click takes back, as a click applies a list in the
 * widget menus. Opening it in the builder is the pencil beside it, on every
 * row: a copy icon on the library's rows read as "duplicate", not "open", and
 * a library list opens as itself anyway — its colour and priority can be kept
 * for you, and only a change to what it holds makes a list of your own.
 *
 * No quick look at its terms, unlike the menus' rows. There a click applies
 * the list, so the icon is the only way to see inside; here the preview says
 * what a list catches and the builder shows everything in it, including the
 * AND/OR rules a quick look flattens — a third icon on every row that
 * answered nothing the other two didn't.
 *
 * `previewing` is marked with a grey fill and a blue edge; `fresh` — the list
 * you just made — with a blue tint, a New chip and a single flare
 * (.nq-wl-fresh). After a save a list is both, and shows both.
 */
export function ListRow({
  entry,
  edit,
  onPreview,
  previewing = false,
  fresh = false,
  ago,
}: {
  entry: ListEntry;
  /** Opens the list in the builder. `title` says what that allows. */
  edit: { title: string; onClick: () => void };
  onPreview: () => void;
  previewing?: boolean;
  fresh?: boolean;
  ago?: string;
}) {
  const name = formatListName(entry.name);
  return (
    <div
      className={clsx(
        "group flex items-center gap-2 rounded px-1 transition-colors",
        fresh && "nq-wl-fresh",
        previewing
          ? "bg-gray-700/60 shadow-[inset_2px_0_0_0_rgba(0,174,239,1)]"
          : fresh
            ? "bg-squawk-blue/10 hover:bg-squawk-blue/[0.16]"
            : "hover:bg-gray-700/30"
      )}
    >
      <button
        type="button"
        onClick={onPreview}
        aria-pressed={previewing}
        title={previewing ? `Stop previewing ${name}` : `See what ${name} catches`}
        // border-0 py-1: the global button style pads and borders buttons,
        // which nearly doubled the row height against the menus' rows.
        className={clsx(
          "min-w-0 flex-1 cursor-pointer truncate border-0 bg-transparent px-0 py-1 text-left text-xs transition-colors hover:text-white",
          previewing ? "font-medium text-white" : "text-gray-200"
        )}
      >
        {name}
      </button>
      {fresh && (
        <span className="shrink-0 rounded bg-squawk-blue/20 px-1.5 py-px text-2xs font-medium text-squawk-blue">
          New
        </span>
      )}
      {ago && (
        <span className="shrink-0 text-2xs tabular-nums text-gray-500">
          {ago}
        </span>
      )}
      {entry.combined && <CombinedChip />}
      <button
        type="button"
        onClick={edit.onClick}
        className="shrink-0 rounded border-0 p-1 text-gray-500 transition-colors hover:bg-gray-700/60 hover:text-squawk-blue"
        title={edit.title}
        aria-label={`Edit ${name}`}
      >
        <PencilSquareIcon className="size-3.5" />
      </button>
    </div>
  );
}

/** A list with the menus' checkbox: the whole row toggles, as it does there. */
export function SelectRow({
  entry,
  checked,
  onToggle,
}: {
  entry: ListEntry;
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded px-1 hover:bg-gray-700/30">
      <span className="flex w-6 shrink-0 justify-center py-1">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="size-3.5 cursor-pointer rounded border-gray-600 bg-gray-900 text-squawk-blue focus:ring-1 focus:ring-squawk-blue focus:ring-offset-0"
        />
      </span>
      <span className="min-w-0 flex-1 truncate py-1 text-xs text-gray-200">
        {formatListName(entry.name)}
      </span>
      {entry.combined && <CombinedChip />}
    </label>
  );
}
